/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useCallback, useReducer, useMemo } from 'react'
import isSolid from 'is-solid'
import { httpAuth } from 'config/apiClient'
import endpoints from '../constants/endpoints'
import { getFileExtension, getUrlParamsString } from '../utils'
import useUploadService from './useUploadService'
import { useDispatch, useSelector } from 'react-redux'
import { IComment, IRefComment, RootState } from 'constants/interfaces'
import { v4 as uuid } from 'uuid'
import { PreviewFile } from 'config/types'
import { allTasksSelector } from 'redux/selectors/tasks'
import keys from 'constants/keys'
import { useFileValidation } from './useFileValidation'
import {
  setComments,
  setCommentsCounter,
  setIsFetchRepliesCounter,
  setIsFetching,
  setViewedCommentsIds,
  updateViews,
} from 'redux/actions/comments'

export const useForceUpdate = () => (useReducer as any)(() => ({}))[1] as () => void

interface ISendComment {
  oid: string
  message: string
  object_type: number
  rg_id: string
  parent_cid?: string
  reply_cid?: string
  object_created_by?: string
  task_id?: string
  subtask_type?: number
  images?: string[]
  videos?: string[]
  notify_groups_ids?: string[]
  auditors?: string[]
  audios?: string[]
  files?: string[]
  is_update_post?: boolean
  groups?: string[]
  users?: string[]
  result_context_id?: string
  result_level?: string
  recurrence_ts?: string
}

export interface ISendCommentParams {
  /** Execution composer must retain its draft if any attachment fails. */
  requireAllAttachments?: boolean
  id: string
  comment: string
  rasID: string
  objectType: number
  parentCID?: string
  replyCID?: string
  createdByUserId?: string
  taskId?: string
  subtaskType?: number
  images?: PreviewFile[]
  videos?: any
  audios?: PreviewFile
  files?: PreviewFile[]
  is_update_post?: boolean
  isPostCommnet?: boolean
  isChat?: boolean
  result_context_id?: string
  result_level?: string
  keyForSave?: string
  recurrence_ts?: string
  ref_comment?: IRefComment | null
}
export interface IFetchComments {
  offset?: number | null
  limit: number | null
  created_ts: number
  cid?: string
  parentCID?: string
  includeCreatedAtTimestamp?: boolean
  queryOlderComments?: boolean
  isManuallyTriggered?: boolean
  id?: string
  mediaHashId?: string
  keyForSave?: string
}

const useComments = (oid: string, isReply?: boolean, keyForSave?: string) => {
  const key = keyForSave || oid
  const dispatch = useDispatch()
  const repliesCounter = useSelector((state: RootState) => state.comments.repliesCounter?.[oid])
  const commentsCounter = useSelector((state: RootState) => state.comments.commentsCounter?.[oid])
  const commentsState = useSelector((state: RootState) => state.comments.commentsState[key])
  const repliesState = useSelector((state: RootState) => state.comments.repliesState[key])
  const posts = useSelector((state: RootState) => state.feed.posts)
  const chats = useSelector((state: RootState) => state.feed.chats)

  const targetCommentsState = useMemo(
    () => (isReply ? repliesState?.items || [] : commentsState?.items || []),
    [commentsState, repliesState, isReply]
  )
  const targetCounterState = useMemo(
    () => (isReply ? repliesCounter || 0 : commentsCounter || 0),
    [isReply, repliesCounter, commentsCounter]
  )

  const isLoadMore = useMemo(() => {
    return isReply ? repliesState?.hasMore || false : commentsState?.hasMore || false
  }, [repliesState?.hasMore, commentsState?.hasMore, isReply])

  const { videoFileHandler } = useUploadService(true)

  // Loading belongs to the requested thread/result, not whichever file is currently selected.
  const [pendingFetches, setPendingFetches] = useState<Record<string, number>>({})
  const fetchKey = `${isReply ? 'reply' : 'comment'}:${key}`
  const fetchCommentsLoading = (pendingFetches[fetchKey] || 0) > 0
  const [commentsCounterLoading, setCommentsCounterLoading] = useState<boolean>(true)
  const [sendCommentLoading, setSendCommentLoading] = useState<boolean>(false)
  const [deleteCommentLoading, setDeleteCommentLoading] = useState<boolean>(false)
  const { imageFileUpload, uploadFile } = useUploadService()
  const { fileSizeValidation } = useFileValidation()
  const allTasks = useSelector(allTasksSelector)

  const fetchComments = useCallback(
    async ({
      offset = null,
      limit = null,
      created_ts,
      cid,
      parentCID,
      includeCreatedAtTimestamp = false,
      queryOlderComments,
      isManuallyTriggered,
      id,
      keyForSave,
    }: IFetchComments) => {
      const requestKey = `${isReply ? 'reply' : 'comment'}:${keyForSave || key}`
      setPendingFetches((pending) => ({ ...pending, [requestKey]: (pending[requestKey] || 0) + 1 }))
      const url = endpoints.comments
      const targetOid = oid
      const paramsObj = {
        oid: targetOid,
        cid,
        created_at_ts: created_ts,
        include_created_at_timestamp: includeCreatedAtTimestamp,
        query_older_comments: queryOlderComments,
        offset,
        limit,
        parent_cid: parentCID,
      }

      const params = getUrlParamsString(paramsObj)

      try {
        const { data } = await httpAuth.get(url + params)
        if (isSolid(data.comments)) {
          // Reverse use for set date from older to new
          dispatch(
            setComments({
              items: [...data.comments.reverse(), ...targetCommentsState],
              hasMore: data.more,
              key: keyForSave || key,
              isReply: isReply,
            })
          )
          if (isManuallyTriggered && id) {
            const viewedIds = data.comments.map((comment: IComment) => comment.cid)
            dispatch(
              updateViews({
                postID: id,
                viewedIds: viewedIds,
              })
            )
            dispatch(
              setViewedCommentsIds({
                key: id,
                ids: viewedIds,
              })
            )
          }
        }
        return Promise.resolve(data.comments)
      } catch (error) {
        console.log('Failed to fetch comments')
        return undefined
      } finally {
        setPendingFetches((pending) => ({ ...pending, [requestKey]: Math.max(0, (pending[requestKey] || 0) - 1) }))
      }
    },
    [oid, targetCommentsState, key, isReply]
  )
  const fetchCommentsCounter = useCallback(
    async (parent_cid?: string, id?: string) => {
      const key = id ? id : oid
      setCommentsCounterLoading(true)
      try {
        const url = endpoints.commentsCounter
        const params = getUrlParamsString({ oid: key, parent_cid })
        const { data } = await httpAuth.get(url + params)

        if (key in data) {
          dispatch(
            setCommentsCounter({
              count: data[key].num_of_comments,
              key: parent_cid ? parent_cid : key,
              isReply: isReply,
            })
          )
          dispatch(setViewedCommentsIds({ key: parent_cid ? parent_cid : key, ids: data[key].viewed_cids }))
        }
      } catch (error) {
        console.log(error)
      } finally {
        setCommentsCounterLoading(false)
      }
    },
    [oid, commentsState?.[oid]?.items]
  )

  const sendComment = useCallback(
    async ({
      id,
      comment,
      rasID,
      objectType,
      parentCID,
      replyCID,
      createdByUserId,
      taskId,
      subtaskType,
      images,
      videos,
      audios,
      files,
      is_update_post,
      result_context_id,
      result_level,
      isPostCommnet = true,
      isChat = false,
      keyForSave,
      recurrence_ts,
      ref_comment,
      requireAllAttachments = false,
    }: ISendCommentParams) => {
      const temporaryCommentId: string = uuid()
      dispatch(setIsFetching(true))
      setSendCommentLoading(true)
      dispatch(setIsFetchRepliesCounter(false))
      try {
        const targetPostsOrChats = isChat ? chats : posts
        const postGroupsAndUsers = isPostCommnet
          ? {
              groups: targetPostsOrChats.filter((post) => post.post_id === id)[0].post_origin_groups,
              users: targetPostsOrChats.filter((post) => post.post_id === id)[0].post_origin_users,
              tags: targetPostsOrChats.filter((post) => post.post_id === id)[0].post_origin_tags,
            }
          : {}
        const url = endpoints.comments
        let body: ISendComment = {
          oid: id,
          message: comment || '',
          object_type: objectType,
          rg_id: rasID,
          is_update_post,
          result_context_id,
          result_level,
          ...(recurrence_ts ? { recurrence_ts } : {}),
          ...postGroupsAndUsers,
          ...(ref_comment ? { ref_comment } : {}),
        }

        const timeCreation = Date.now() / 1000

        const temporaryComment: IComment = {
          ...body,
          isDummy: true,
          cid: temporaryCommentId,
          created_at_ts: timeCreation,
          parent_cid: parentCID || oid,
          object_type: objectType,
          created_by: createdByUserId || '',
          updated_at_ts: timeCreation,
          result_context_id: result_context_id,
          result_level,
          reply_to: '',
          reply_cid: '',
        }

        if (taskId) {
          const task = allTasks?.find((task) => task.tid === taskId)
          body.notify_groups_ids = task ? task.notify_groups_ids : []
          if (objectType === keys.COMMENT_OBJECT_TYPE.COMMENT_ON_IMAGE || objectType === 23) {
            body.auditors = task ? task.auditors ?? [] : []
          }
        }

        if (parentCID) {
          body.parent_cid = parentCID
        }
        if (replyCID) {
          body.reply_cid = replyCID
        }
        if (createdByUserId) {
          body.object_created_by = createdByUserId
        }
        if (taskId) {
          body.task_id = taskId
        }

        if (videos) {
          let uploadedVideos: string[] = []

          for (let video of videos) {
            try {
              const isValid = fileSizeValidation(video.file)
              if (!isValid) {
                setSendCommentLoading(false)
                return
              }
              const uploadedLink = requireAllAttachments
                ? video.uploadedUrl || (await uploadFile(video.file, getFileExtension(video.file.name)))
                : await videoFileHandler(video.file)
              if (requireAllAttachments && !uploadedLink) throw new Error('Video upload failed')
              uploadedVideos = [...uploadedVideos, uploadedLink!]
            } catch (error) {
              if (requireAllAttachments) throw error
              console.log(error)
            }
          }

          body.videos = uploadedVideos
        }

        if (Number(subtaskType) >= 0) {
          body.subtask_type = subtaskType
        }

        const mediaPromises: Record<string, Promise<string[]>> = {}

        if (images) {
          temporaryComment.images = images.map((image) => image.previewUrl)
          const areFilesValid = images.every((image) => fileSizeValidation(image.file))
          if (!areFilesValid) {
            setSendCommentLoading(false)
            return
          }
          const imagePromises = images.map((image) =>
            requireAllAttachments && image.uploadedUrl
              ? Promise.resolve(image.uploadedUrl)
              : imageFileUpload(image.file)
          )
          mediaPromises['images'] = Promise.all(imagePromises)
        }
        if (audios) {
          temporaryComment.audios = [audios.previewUrl]
          const isValid = fileSizeValidation(audios.file)
          if (!isValid) {
            setSendCommentLoading(false)
            return
          }
          const extention = getFileExtension(audios.file.name)
          const audioPromise = uploadFile(audios.file, extention)
          mediaPromises['audios'] = audioPromise.then((url) => [url])
        }
        if (files) {
          temporaryComment.files = files.map((file) => file.previewUrl)
          const isValid = files.every((file) => fileSizeValidation(file.file))
          if (!isValid) {
            setSendCommentLoading(false)
            return
          }
          const filePromises = files.map((file) =>
            requireAllAttachments && file.uploadedUrl
              ? Promise.resolve(file.uploadedUrl)
              : uploadFile(file.file, getFileExtension(file.file.name))
          )
          mediaPromises['files'] = Promise.all(filePromises)
        }
        const keyforSave = keyForSave ? keyForSave : oid
        dispatch(setComments({ items: [...targetCommentsState, temporaryComment], key: keyforSave, isReply: isReply }))
        const commentsWithoutTemporary = targetCommentsState.filter((comment) => comment.cid !== temporaryCommentId)

        const response = Promise.all(Object.values(mediaPromises))
          .then((data) => {
            const keys = Object.keys(mediaPromises)
            keys.forEach((key, index) => {
              if (requireAllAttachments && (!Array.isArray(data[index]) || data[index].some((url: string) => !url))) {
                throw new Error('Attachment upload failed')
              }
              body[key] = data[index]
            })
            return httpAuth.post(url, body)
          })

          .then(({ data }) => {
            const commentsWithoutTemporary = targetCommentsState.filter((comment) => comment.cid !== temporaryCommentId)
            dispatch(setComments({ items: [...commentsWithoutTemporary, data], key: keyforSave, isReply: isReply }))

            dispatch(
              setViewedCommentsIds({
                key: id,
                ids: [data.cid],
              })
            )
            dispatch(setIsFetchRepliesCounter(true))
            return data.cid
          })
          .catch((error) => {
            console.log(error)
            dispatch(setComments({ items: commentsWithoutTemporary, key: keyforSave, isReply: isReply }))
            return false
          })
          .finally(() => {
            setSendCommentLoading(() => false)
            dispatch(setIsFetching(false))
          })
        return Promise.resolve(response)
      } catch (error) {
        console.log(error)
        setSendCommentLoading(false)
        dispatch(setIsFetching(false))
        return false
      }
    },
    [oid, targetCommentsState, commentsCounter]
  )

  const deleteComment = useCallback(
    async ({ id, cid, reply_cid, isReplyed }: { id: string; cid: string; reply_cid?: string; isReplyed?: boolean }) => {
      setDeleteCommentLoading(true)
      try {
        const c_cid = reply_cid ? reply_cid : cid
        const url = endpoints.comments
        httpAuth.delete(url, {
          data: { oid: id, cid: c_cid },
        })

        const filteredComments = targetCommentsState.filter((comment) => comment.cid !== c_cid)
        if (isReplyed) {
          dispatch(setComments({ items: filteredComments, key: cid, isReply: true }))
        } else {
          dispatch(setComments({ items: filteredComments, key: key, isReply: false }))
        }
      } catch (error) {
        console.log(error)
      } finally {
        setDeleteCommentLoading(false)
      }
    },
    [targetCommentsState, oid]
  )

  return {
    fetchComments,
    fetchCommentsCounter,
    sendComment,
    deleteComment,
    deleteCommentLoading,
    comments: targetCommentsState,
    setComments,
    commentsCounter: targetCounterState,
    fetchCommentsLoading,
    commentsCounterLoading,
    sendCommentLoading,
    isLoadMore,
  }
}

export default useComments
